import React, { useEffect, useRef, useState } from 'react';
import { Share2, Copy, ExternalLink, Play, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PERSONAS, VOICE_OPTIONS } from '../constants/personas';

export default function Branding() {
  const { agent, patchAgent } = useAuth();
  const [savingAi, setSavingAi] = useState(false);
  const [previewingVoiceId, setPreviewingVoiceId] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const previewAudioRef = useRef(null);
  const [aiConfig, setAiConfig] = useState({
    assistant_name: '',
    avatar_filename: 'ava-avatar.png',
    voice_id: '',
    clientele_profile: 'balanced',
    personality_preset: 'professional_warm',
    auto_switch_tone: true,
    dashboard_theme: 'dark',
    greeting_message: '',
    brand_tagline: '',
    public_bio: '',
    brand_website: '',
    about_link_label: '',
    brand_primary_color: '#C2410C',
    brand_secondary_color: '#9c7a3f',
  });
  const [hasAgentPhoto, setHasAgentPhoto] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', again: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  const publicChatUrl = agent?.slug ? `${window.location.origin}/chat/${agent.slug}` : '';
  const agentPhotoUrl = agent?.id ? `/api/auth/agent-photo/${agent.id}?v=${photoVersion}` : '';

  useEffect(() => {
    api.get('/agent-ai-config')
      .then((res) => {
        const cfg = res.data?.config || {};
        setAiConfig({
          assistant_name: cfg.assistant_name || agent?.assistant_name || 'Ava',
          avatar_filename: cfg.avatar_filename || 'ava-avatar.png',
          voice_id: cfg.voice_id || '',
          clientele_profile: cfg.clientele_profile || 'balanced',
          personality_preset: cfg.personality_preset || 'professional_warm',
          auto_switch_tone: cfg.auto_switch_tone !== false,
          dashboard_theme: cfg.dashboard_theme || 'dark',
          greeting_message: cfg.greeting_message || '',
          brand_tagline: cfg.brand_tagline || '',
          public_bio: cfg.public_bio || '',
          brand_website: cfg.brand_website || '',
          about_link_label: cfg.about_link_label || '',
          brand_primary_color: cfg.brand_primary_color || '#C2410C',
          brand_secondary_color: cfg.brand_secondary_color || '#9c7a3f',
        });
        setHasAgentPhoto(!!cfg.has_agent_photo);
      })
      .catch(() => {
        setAiConfig((prev) => ({
          ...prev,
          assistant_name: agent?.assistant_name || prev.assistant_name || 'Ava',
        }));
      });
  }, [agent?.assistant_name]);

  const handleAiConfigChange = (field, value) => {
    setAiConfig((prev) => ({ ...prev, [field]: value }));
  };

  // The dropdown mirrors the sidebar sun/moon toggle — same instant save via
  // the scoped theme endpoint, so it doesn't sit stuck until the full
  // "Save AI settings" click like the rest of this form does.
  const changeDashboardMode = async (nextTheme) => {
    const prevTheme = aiConfig.dashboard_theme;
    handleAiConfigChange('dashboard_theme', nextTheme);
    patchAgent({ dashboard_theme: nextTheme });
    try {
      await api.patch('/agent-ai-config/theme', { dashboard_theme: nextTheme });
    } catch {
      handleAiConfigChange('dashboard_theme', prevTheme);
      patchAgent({ dashboard_theme: prevTheme });
      toast.error('Could not switch dashboard mode.');
    }
  };

  // Name, avatar, and voice always move together — the only way to get a
  // mismatched combination is to deliberately open "Customize" afterward.
  const selectPersona = (persona) => {
    setAiConfig((prev) => ({
      ...prev,
      assistant_name: persona.name,
      avatar_filename: persona.file,
      voice_id: persona.voiceId,
    }));
    setShowCustomize(false);
    playVoicePreview(persona.voiceId, persona.name);
  };

  const saveAiConfig = async (e) => {
    e.preventDefault();
    if (!aiConfig.assistant_name.trim()) {
      toast.error('Assistant name is required.');
      return;
    }
    setSavingAi(true);
    try {
      const payload = {
        assistant_name: aiConfig.assistant_name.trim(),
        avatar_filename: aiConfig.avatar_filename.trim(),
        voice_id: aiConfig.voice_id.trim(),
        clientele_profile: aiConfig.clientele_profile,
        personality_preset: aiConfig.personality_preset,
        auto_switch_tone: aiConfig.auto_switch_tone,
        dashboard_theme: aiConfig.dashboard_theme,
        greeting_message: aiConfig.greeting_message.trim(),
        brand_tagline: aiConfig.brand_tagline.trim(),
        public_bio: aiConfig.public_bio.trim(),
        brand_website: aiConfig.brand_website.trim(),
        about_link_label: aiConfig.about_link_label.trim(),
        brand_primary_color: aiConfig.brand_primary_color,
        brand_secondary_color: aiConfig.brand_secondary_color,
      };
      const res = await api.put('/agent-ai-config', payload);
      const cfg = res.data?.config || payload;
      setAiConfig({
        assistant_name: cfg.assistant_name || 'Ava',
        avatar_filename: cfg.avatar_filename || 'ava-avatar.png',
        voice_id: cfg.voice_id || '',
        clientele_profile: cfg.clientele_profile || 'balanced',
        personality_preset: cfg.personality_preset || 'professional_warm',
        auto_switch_tone: cfg.auto_switch_tone !== false,
        dashboard_theme: cfg.dashboard_theme || 'dark',
        greeting_message: cfg.greeting_message || '',
        brand_tagline: cfg.brand_tagline || '',
        public_bio: cfg.public_bio || '',
        brand_website: cfg.brand_website || '',
        about_link_label: cfg.about_link_label || '',
        brand_primary_color: cfg.brand_primary_color || '#C2410C',
        brand_secondary_color: cfg.brand_secondary_color || '#9c7a3f',
      });
      patchAgent({
        assistant_name: cfg.assistant_name || 'Ava',
        avatar_filename: cfg.avatar_filename || 'ava-avatar.png',
        voice_id: cfg.voice_id || '',
        clientele_profile: cfg.clientele_profile || 'balanced',
        personality_preset: cfg.personality_preset || 'professional_warm',
        auto_switch_tone: cfg.auto_switch_tone !== false,
        dashboard_theme: cfg.dashboard_theme || 'dark',
        brand_tagline: cfg.brand_tagline || '',
        public_bio: cfg.public_bio || '',
        brand_website: cfg.brand_website || '',
        about_link_label: cfg.about_link_label || '',
        brand_primary_color: cfg.brand_primary_color || '#C2410C',
        brand_secondary_color: cfg.brand_secondary_color || '#9c7a3f',
      });
      toast.success('Branding settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save branding settings.');
    } finally {
      setSavingAi(false);
    }
  };

  const uploadAgentPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      await api.post('/agent-ai-config/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHasAgentPhoto(true);
      setPhotoVersion((v) => v + 1);
      toast.success('Photo updated.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removeAgentPhoto = async () => {
    setUploadingPhoto(true);
    try {
      await api.delete('/agent-ai-config/photo');
      setHasAgentPhoto(false);
      setPhotoVersion((v) => v + 1);
      toast.success('Photo removed.');
    } catch (err) {
      toast.error('Could not remove photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const playVoicePreview = async (voiceId, nameOverride) => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewingVoiceId(voiceId || 'default');
    try {
      const name = (nameOverride || aiConfig.assistant_name).trim() || 'Ava';
      const sampleText = `Hi, I'm ${name}. I can help you explore homes and book a showing anytime.`;
      const res = await api.post(
        '/voice/speak',
        { text: sampleText, agent_id: agent?.id, voice_id: voiceId || undefined },
        { responseType: 'arraybuffer', timeout: 20000 }
      );
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audioEl = new Audio(url);
      previewAudioRef.current = audioEl;
      audioEl.onended = () => {
        URL.revokeObjectURL(url);
        if (previewAudioRef.current === audioEl) previewAudioRef.current = null;
        setPreviewingVoiceId(null);
      };
      audioEl.onerror = () => {
        URL.revokeObjectURL(url);
        setPreviewingVoiceId(null);
      };
      await audioEl.play();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not play a preview for this voice.');
      setPreviewingVoiceId(null);
    }
  };

  const copyPublicChatLink = async () => {
    if (!publicChatUrl) return;
    try {
      await navigator.clipboard.writeText(publicChatUrl);
      toast.success('Public chat link copied.');
    } catch {
      toast.error('Could not copy link.');
    }
  };

  const sectionHeadingStyle = {
    fontSize: 13,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--text-muted)',
    margin: '28px 0 12px',
    paddingTop: 20,
    borderTop: '1px solid var(--border)',
  };

  return (
    <div>
      <div className="page-header">
        <h2>Branding</h2>
        <p>Control your public chat link, assistant identity, voice, and tone profile.</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Share2 size={16} /> Share your buyer chat
          </span>
        </div>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65, margin: '0 0 12px' }}>
          Share your public chat link so buyers and sellers can start with {agent?.assistant_name || 'Ava'} anytime.
        </p>
        {publicChatUrl && (
          <div style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px solid var(--border)',
            fontSize: 13,
            color: 'var(--text-secondary)',
            wordBreak: 'break-all',
          }}
          >
            {publicChatUrl}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost" onClick={copyPublicChatLink} disabled={!publicChatUrl}>
            <Copy size={14} /> Copy link
          </button>
          {agent?.slug && (
            <a
              href={`/chat/${agent.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}
            >
              Preview your buyer chat <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">AI assistant branding</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          Choose your assistant's persona, upload your own photo, and set your personal brand details and default clientele tone profile.
        </p>
        <h4 style={{ ...sectionHeadingStyle, marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Look &amp; feel</h4>
        <div className="form-group" style={{ marginBottom: 12, maxWidth: 280 }}>
          <label className="form-label">Dashboard mode</label>
          <select
            className="form-input"
            value={aiConfig.dashboard_theme}
            onChange={(e) => changeDashboardMode(e.target.value)}
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Switches instantly, same as the sun/moon toggle in the sidebar — no need to hit Save.
          </div>
        </div>
        <form onSubmit={saveAiConfig}>
          <h4 style={{ ...sectionHeadingStyle, marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Identity</h4>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <label className="form-label">Assistant persona</label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Name, face, and voice come as one — no risk of ending up with a mismatched name or voice on the wrong avatar.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10, maxWidth: 480 }}>
              {PERSONAS.map((p) => {
                const isSelected = aiConfig.avatar_filename === p.file
                  && aiConfig.assistant_name === p.name
                  && aiConfig.voice_id === p.voiceId;
                return (
                  <button
                    key={p.file}
                    type="button"
                    onClick={() => selectPersona(p)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 6,
                      padding: '10px 6px',
                      borderRadius: 10,
                      border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSelected ? 'var(--accent-dim)' : 'var(--bg-card)',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={`/${p.file}`}
                      alt=""
                      style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <span style={{ fontSize: 12.5, textTransform: 'none', color: 'var(--text-primary)' }}>{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setShowCustomize((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent-light)',
                fontSize: 12.5,
                textTransform: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {showCustomize ? 'Hide customize options' : 'Want a different name or voice than the persona? Customize'}
            </button>
            {showCustomize && (
              <div style={{ marginTop: 10, padding: 12, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)' }}>
                <div className="form-group" style={{ marginBottom: 12, maxWidth: 300 }}>
                  <label className="form-label">Custom assistant name</label>
                  <input
                    className="form-input"
                    value={aiConfig.assistant_name}
                    onChange={(e) => handleAiConfigChange('assistant_name', e.target.value)}
                    placeholder="e.g. Ava"
                    required
                  />
                </div>
                <label className="form-label">Custom voice</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {VOICE_OPTIONS.map((opt) => {
                    const isSelected = aiConfig.voice_id === opt.id;
                    const isPreviewing = previewingVoiceId === (opt.id || 'default');
                    return (
                      <label
                        key={opt.id || 'default'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                          background: isSelected ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name="voice_id"
                          checked={isSelected}
                          onChange={() => handleAiConfigChange('voice_id', opt.id)}
                          style={{ width: 16, height: 16, flex: '0 0 auto', padding: 0, border: 'none', background: 'none' }}
                        />
                        <span style={{ flex: 1, textAlign: 'left', textTransform: 'none' }}>{opt.label}</span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: 12, textTransform: 'none' }}
                          disabled={previewingVoiceId !== null}
                          onClick={(e) => {
                            e.preventDefault();
                            playVoicePreview(opt.id);
                          }}
                        >
                          {isPreviewing ? <Loader2 size={13} className="icon-spin" /> : <Play size={13} />}
                          {isPreviewing ? 'Playing…' : 'Play sample'}
                        </button>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Your photo</label>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 10 }}>
              Shown on your public chat page. JPEG, PNG, or WebP, up to 3MB.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {hasAgentPhoto ? (
                  <img
                    src={agentPhotoUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'none' }}>No photo</span>
                )}
              </div>
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                {uploadingPhoto ? 'Uploading…' : hasAgentPhoto ? 'Replace photo' : 'Upload photo'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={uploadingPhoto}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    uploadAgentPhoto(file);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </label>
              {hasAgentPhoto && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={uploadingPhoto}
                  onClick={removeAgentPhoto}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Opening greeting</label>
            <textarea
              className="form-input"
              rows={3}
              value={aiConfig.greeting_message}
              onChange={(e) => handleAiConfigChange('greeting_message', e.target.value)}
              placeholder="What buyers see when they open your chat. Leave blank for the default."
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Brand tagline</label>
            <input
              className="form-input"
              value={aiConfig.brand_tagline}
              onChange={(e) => handleAiConfigChange('brand_tagline', e.target.value)}
              placeholder="e.g. Local expertise, trusted guidance"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Website / About page URL</label>
            <input
              className="form-input"
              value={aiConfig.brand_website}
              onChange={(e) => handleAiConfigChange('brand_website', e.target.value)}
              placeholder="e.g. sarahmurphy.ca/about"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">About link label</label>
            <input
              className="form-input"
              value={aiConfig.about_link_label}
              onChange={(e) => handleAiConfigChange('about_link_label', e.target.value)}
              placeholder="e.g. About Sarah"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Public short bio</label>
            <textarea
              className="form-input"
              value={aiConfig.public_bio}
              onChange={(e) => handleAiConfigChange('public_bio', e.target.value)}
              rows={3}
              placeholder="e.g. Sarah has helped families and first-time buyers across St. John's and surrounding communities for over 12 years."
            />
          </div>

          <h4 style={sectionHeadingStyle}>Personality</h4>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Assistant personality</label>
            <select
              className="form-input"
              value={aiConfig.personality_preset}
              onChange={(e) => handleAiConfigChange('personality_preset', e.target.value)}
            >
              <option value="professional_warm">Professional &amp; warm (recommended)</option>
              <option value="polished_professional">Polished professional</option>
              <option value="warm_neighbourly">Warm &amp; neighbourly</option>
              <option value="concise_efficient">Concise &amp; efficient</option>
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              How your assistant sounds. Honesty, fair-housing, and refer-to-you rules always apply regardless of personality.
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Clientele tone profile</label>
            <select
              className="form-input"
              value={aiConfig.clientele_profile}
              onChange={(e) => handleAiConfigChange('clientele_profile', e.target.value)}
            >
              <option value="balanced">Balanced (general market)</option>
              <option value="first_time">First-time buyer focused</option>
              <option value="luxury_executive">Luxury / executive</option>
              <option value="urban_downtown">Urban / downtown</option>
              <option value="rural_country">Rural / country</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={Boolean(aiConfig.auto_switch_tone)}
                onChange={(e) => handleAiConfigChange('auto_switch_tone', e.target.checked)}
              />
              Auto-switch tone by lead context
            </label>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              When enabled, Ava starts with your selected clientele profile, then adapts tone by buyer signals like budget, first-time cues, and urban/rural priorities.
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={savingAi}>
            {savingAi ? 'Saving…' : 'Save AI settings'}
          </button>
        </form>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Password</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
          This is the password for this portal. It does not change a buyer&apos;s login.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (passwordForm.next.length < 8) {
              toast.error('Use at least 8 characters.');
              return;
            }
            if (passwordForm.next !== passwordForm.again) {
              toast.error('The new passwords do not match.');
              return;
            }
            setSavingPassword(true);
            try {
              await api.post('/auth/password', {
                current_password: passwordForm.current,
                new_password: passwordForm.next,
              });
              setPasswordForm({ current: '', next: '', again: '' });
              toast.success('Password changed.');
            } catch (err) {
              toast.error(err?.response?.data?.error || 'Could not change the password');
            } finally {
              setSavingPassword(false);
            }
          }}
        >
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label" htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              type="password"
              className="form-input"
              autoComplete="current-password"
              value={passwordForm.current}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label" htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, next: e.target.value }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label" htmlFor="confirm-password">New password again</label>
            <input
              id="confirm-password"
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={passwordForm.again}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, again: e.target.value }))}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={savingPassword}>
            {savingPassword ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}
